import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { AppTheme } from '@/shared/constant/AppTheme';
import { ErrorCode } from '@/shared/constant/error/ErrorCode';
import type { ErrorCodeMessageType } from '@/shared/constant/error/ErrorCodeMessageType';
import { OnlineType } from '@/shared/constant/OnlineType';
import { isClient } from '@/shared/util/ssr';
import { DocsSiteVisibilitySettingsType } from '@tarslib/types/lib/publishedProject';
import clsx from 'clsx';
import { CustomAuth } from './components/custom-auth/CustomAuth';
import EmailValidator from './EmailValidator';
import { IpError } from './IpError';
import PasswordValidator from './PasswordValidator';

type DocsSiteAuthProps = {
  value: ErrorCodeMessageType;
};
const DocsSiteAuth = (props: DocsSiteAuthProps) => {
  const { value } = props;
  const getAppearanceConfigModelStoreState = useAppearanceConfigModelStore((x) => x.getState);

  if (value.errorCode !== ErrorCode.Unauthorized) {
    return null;
  }

  const logoSettings = value.extra?.logoSettings;
  const title = value.extra?.title;
  const description = value.extra?.description;
  const onlineType = value.extra?.onlineType;
  const onlineId = value.extra?.onlineId;
  const projectId = value.extra?.projectId;
  const visibilitySettings = value.extra?.visibilitySettings;
  const appTheme = getAppearanceConfigModelStoreState().appTheme;
  const logo = logoSettings?.[isClient ? window.__currentTheme : (appTheme ?? AppTheme.Light)];
  const visitType = value.extra?.visitType;
  const subdirectory = value.extra?.subdirectory;

  if (!projectId || !onlineId) {
    return null;
  }

  const imgRender = () => {
    return <img src={logo} alt="" className={clsx('mb-8 block h-16 rounded-lg object-cover')} />;
  };

  if (onlineType === OnlineType.SharedDoc) {
    return (
      <PasswordValidator
        id={onlineId}
        imgRender={imgRender}
        name={title}
        description={description}
        projectId={projectId}
        onlineType={OnlineType.SharedDoc}
        subdirectory={subdirectory}
        visitType={visitType}
      />
    );
  }

  const templatePageContent = () => {
    let result = <></>;
    switch (visibilitySettings?.type) {
      case DocsSiteVisibilitySettingsType.PASSWORD_PROTECTED:
        result = (
          <PasswordValidator
            id={onlineId}
            imgRender={imgRender}
            name={title}
            description={description}
            projectId={projectId}
            onlineType={OnlineType.DocsSite}
            subdirectory={subdirectory}
            visitType={visitType}
          />
        );
        break;
      case DocsSiteVisibilitySettingsType.EMAIL_WHITELIST:
        result = (
          <EmailValidator
            id={onlineId}
            imgRender={imgRender}
            name={title}
            description={description}
            projectId={projectId}
            subdirectory={subdirectory}
            visitType={visitType}
            visibilitySettings={visibilitySettings}
          />
        );
        break;
      case DocsSiteVisibilitySettingsType.IP_WHITELIST:
        result = <IpError />;
        break;
      case DocsSiteVisibilitySettingsType.CUSTOM_AUTH:
        result = (
          <CustomAuth
            docsSiteId={Number(onlineId)}
            projectId={projectId}
            subdirectory={subdirectory}
            visitType={visitType}
            visibilitySettings={visibilitySettings}
          />
        );
        break;
      default:
        break;
    }
    return result;
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      {templatePageContent()}
    </div>
  );
};

export default DocsSiteAuth;
